import React from 'react';

/**
 * BlogSketch — one flat editorial illustration per diary article.
 * All scenes share the site's pastel palette and a 20:13 aspect.
 * The "perche-un-bradipo" article uses the actual Lempole sloth PNG.
 */

const SLOTH_URL = 'https://customer-assets-eiarnc6j.emergentagent.net/job_lempole-design/artifacts/2hq5tlkb_lempole-sloth.png';

const P = {
  sky:      '#f4e6d2', sky2: '#e8d3b3', cloud: '#fbf6ea',
  hillFar:  '#b8c9a2', hillMid: '#9fb583', hillNear: '#7d9a68',
  field:    '#e6c880', fieldD: '#c9a463',
  earth:    '#c69078', earthD: '#8a5540',
  stone:    '#c7b394', stoneD: '#7a6b52',
  rose:     '#ecb5a0', wine: '#7a3520',
  cream:    '#fbf6ea', ink: '#3a2418',
  cyan:     '#c7d6d8', gold: '#d9a75a',
  leaf:     '#88a874', leafD: '#5f7a4a',
  night:    '#334c5a', moon: '#f0e6cd', iris: '#a480b5',
  steel:    '#b7bcc0', steelD: '#7d848b',
};

const VB = '0 0 200 130';

/* Helper: cream/warm sky gradient. */
const Sky = ({ id }) => (
  <defs>
    <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%" stopColor={P.sky} />
      <stop offset="100%" stopColor={P.sky2} />
    </linearGradient>
  </defs>
);

/* ------------------ 18 unique scenes -------------------- */

// 1. Perché un bradipo — actual sloth logo on a soft green branch canopy
function SBradipo({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <path d="M0 88 Q60 76 130 84 T200 82 L200 130 L0 130Z" fill={P.hillMid}/>
      <path d="M0 105 Q40 95 90 100 T200 98 L200 130 L0 130Z" fill={P.hillNear}/>
      {/* branch across */}
      <path d="M0 62 Q100 45 200 58" fill="none" stroke={P.earthD} strokeWidth="3" strokeLinecap="round"/>
      {[[26,56],[54,50],[78,48],[132,50],[168,54]].map(([x,y],i)=>(
        <ellipse key={i} cx={x} cy={y} rx="5" ry="2.7" fill={P.leaf} transform={`rotate(-18 ${x} ${y})`}/>
      ))}
      {/* the actual Lempole sloth PNG */}
      <image href={SLOTH_URL} x="72" y="42" width="56" height="56" preserveAspectRatio="xMidYMid meet"/>
    </>
  );
}

// 2. Lamole — village silhouette at sunset with medieval tower
function SLamole({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#f6d5a8"/><stop offset="60%" stopColor="#eeb69b"/><stop offset="100%" stopColor="#e69b7c"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <circle cx="150" cy="55" r="12" fill={P.cream} opacity="0.85"/>
      <path d="M0 80 Q40 68 90 76 T200 74 L200 130 L0 130Z" fill={P.hillFar} opacity="0.85"/>
      {/* stone village on hill */}
      <path d="M0 100 Q40 88 100 92 T200 94 L200 130 L0 130Z" fill={P.hillMid}/>
      {/* tower */}
      <rect x="94" y="70" width="7" height="24" fill={P.stone}/>
      <polygon points="93,70 102,70 97.5,64" fill={P.wine}/>
      <rect x="96" y="76" width="2" height="4" fill={P.ink} opacity="0.6"/>
      {/* houses cluster */}
      {[[70,84],[80,86],[108,84],[118,86],[128,88]].map(([x,y],i)=>(
        <g key={i}><rect x={x} y={y} width="8" height="8" fill={P.cream}/><polygon points={`${x-1},${y} ${x+9},${y} ${x+4},${y-4}`} fill={P.wine}/></g>
      ))}
      {/* cypresses */}
      {[52,140,155].map((x,i)=><ellipse key={i} cx={x} cy="98" rx="3" ry="11" fill={P.leafD}/>)}
    </>
  );
}

// 3. Viticoltura eroica — steep terrace cross-section
function STerraceEroica({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <path d="M0 60 Q60 50 130 58 T200 52 L200 130 L0 130Z" fill={P.hillFar}/>
      {/* stepped terraces going up-right */}
      {[[0,110,80,10],[40,95,90,10],[80,80,90,10],[120,65,80,10]].map(([x,y,w,h],i)=>(
        <g key={i}>
          <rect x={x} y={y} width={w} height={h} fill={P.hillMid} opacity={0.9-i*0.06}/>
          <rect x={x} y={y+h-2} width={w} height="3" fill={P.stone}/>
          {Array.from({length:Math.floor(w/12)}).map((_,k)=>(
            <g key={k} transform={`translate(${x+8+k*12} ${y+3})`}>
              <rect x="-0.6" y="0" width="1.2" height="5" fill={P.earthD}/>
              <circle cx="0" cy="0" r="2.5" fill={P.leaf}/>
            </g>
          ))}
        </g>
      ))}
      {/* small human figure — heroic worker */}
      <g transform="translate(150 58)"><circle r="2" cx="0" cy="0" fill={P.ink}/><rect x="-1" y="2" width="2" height="5" fill={P.wine}/></g>
    </>
  );
}

// 4. Muretti a secco — close up of dry stone wall
function SMuretto({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <path d="M0 55 Q80 45 200 55 L200 130 L0 130Z" fill={P.hillFar}/>
      {/* stone wall as tiled irregular blocks */}
      <rect x="10" y="70" width="180" height="46" fill={P.stone}/>
      {(() => {
        const stones = [];
        let y = 72;
        for (let row = 0; row < 6; row++) {
          let x = 12 + (row%2)*4;
          while (x < 188) {
            const w = 8 + Math.floor(Math.abs(Math.sin(row*7+x))*8);
            const h = 5 + Math.floor(Math.abs(Math.cos(x))*3);
            stones.push(<rect key={row+'-'+x} x={x} y={y} width={w} height={h} fill={P.stoneD} opacity="0.55" stroke={P.cream} strokeWidth="0.6"/>);
            x += w + 1.2;
          }
          y += 7.5;
        }
        return stones;
      })()}
      {/* Vine sprig on top */}
      <g transform="translate(160 68)"><path d="M0 0 Q4 -8 10 -6" fill="none" stroke={P.leafD} strokeWidth="1.2"/><ellipse cx="10" cy="-6" rx="3" ry="2" fill={P.leaf}/></g>
    </>
  );
}

// 5. Sangiovese rosato — bottle riding a wave (contro corrente)
function SRosato({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={P.sky}/><stop offset="100%" stopColor="#fbf6ea"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      {/* Two wave curves opposing */}
      <path d="M0 110 Q50 92 100 108 T200 106 L200 130 L0 130Z" fill={P.cyan}/>
      <path d="M0 122 Q60 116 120 122 T200 120 L200 130 L0 130Z" fill={P.rose} opacity="0.85"/>
      {/* Bottle upright on top */}
      <g transform="translate(100 30) rotate(-6)">
        <path d="M-10 15 L-10 76 Q-10 84 -3 84 L3 84 Q10 84 10 76 L10 15 L7 15 L7 4 Q7 1 4 1 L-4 1 Q-7 1 -7 4 L-7 15Z" fill={P.rose}/>
        <rect x="-8" y="42" width="16" height="24" fill={P.cream} rx="1"/>
        <rect x="-8" y="42" width="16" height="5" fill={P.wine} opacity="0.85"/>
        <circle cx="0" cy="56" r="2.5" fill={P.wine} opacity="0.85"/>
        <rect x="-4" y="-3" width="8" height="6" fill={P.ink} rx="1"/>
      </g>
    </>
  );
}

// 6. Bio alta quota — elevation profile with 600m marker
function SBio({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      {/* Elevation profile — jagged mountain shape climbing to peak */}
      <path d="M0 118 L30 108 L60 96 L90 80 L120 60 L150 46 L180 60 L200 66 L200 130 L0 130Z" fill={P.hillMid}/>
      <path d="M0 118 L30 108 L60 96 L90 80 L120 60 L150 46 L180 60 L200 66" fill="none" stroke={P.hillNear} strokeWidth="2"/>
      {/* 600 m marker at peak */}
      <line x1="150" y1="46" x2="150" y2="30" stroke={P.ink} strokeWidth="1" strokeDasharray="2 2" opacity="0.55"/>
      <rect x="132" y="18" width="36" height="14" fill={P.cream} stroke={P.ink} strokeOpacity="0.4" rx="2"/>
      <text x="150" y="28" fontSize="9" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fill={P.wine} textAnchor="middle">600 m</text>
      {/* leaves scattered */}
      {[[40,120],[75,112],[110,105],[168,88]].map(([x,y],i)=>(
        <ellipse key={i} cx={x} cy={y} rx="3" ry="1.6" fill={P.leaf} transform={`rotate(${-20+i*10} ${x} ${y})`}/>
      ))}
      <circle cx="150" cy="46" r="2" fill={P.wine}/>
    </>
  );
}

// 7. Biodiversità — iris flowers and a bee among vines
function SBiodiv({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <rect x="0" y="80" width="200" height="50" fill={P.hillMid}/>
      {/* vine trellis */}
      {[40,100,160].map((x,i)=>(
        <g key={i} transform={`translate(${x} 0)`}>
          <rect x="-1" y="60" width="2" height="40" fill={P.earthD}/>
          <ellipse cx="-6" cy="78" rx="6" ry="4" fill={P.leaf}/>
          <ellipse cx="6" cy="80" rx="5" ry="3.5" fill={P.leafD}/>
        </g>
      ))}
      {/* iris flowers foreground */}
      {[[20,110],[70,116],[130,110],[180,116]].map(([x,y],i)=>(
        <g key={i} transform={`translate(${x} ${y})`}>
          <line x1="0" y1="0" x2="0" y2="10" stroke={P.leafD} strokeWidth="1.2"/>
          <path d="M0 0 Q-5 -6 -3 -3 M0 0 Q5 -6 3 -3 M0 0 Q0 -8 0 -8" fill="none" stroke={P.iris} strokeWidth="2.4" strokeLinecap="round"/>
          <ellipse cx="0" cy="-5" rx="3" ry="4" fill={P.iris}/>
          <circle cx="0" cy="-4" r="1" fill={P.gold}/>
        </g>
      ))}
      {/* bee */}
      <g transform="translate(155 60)">
        <ellipse rx="3" ry="2" fill={P.gold}/>
        <rect x="-3" y="-0.6" width="6" height="1.2" fill={P.ink}/>
        <ellipse cx="-1" cy="-2" rx="2" ry="1" fill={P.cream} opacity="0.85"/>
      </g>
    </>
  );
}

// 8. Escursione termica — split day/night sky over Lamole
function SEscur({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={P.night}/><stop offset="50%" stopColor="#7a8ea1"/><stop offset="100%" stopColor={P.sky}/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      {/* moon left, sun right */}
      <circle cx="40" cy="42" r="10" fill={P.moon}/>
      <circle cx="34" cy="38" r="3.5" fill={P.night} opacity="0.35"/>
      <circle cx="160" cy="46" r="12" fill={P.cream} opacity="0.9"/>
      {/* stars */}
      {[[16,20],[26,52],[54,26]].map(([x,y],i)=><circle key={i} cx={x} cy={y} r="0.8" fill={P.cream}/>)}
      {/* hills */}
      <path d="M0 92 Q60 80 130 88 T200 84 L200 130 L0 130Z" fill={P.hillMid}/>
      <path d="M0 110 Q40 100 100 106 T200 104 L200 130 L0 130Z" fill={P.hillNear}/>
      {/* thermometer icon centre */}
      <g transform="translate(100 60)">
        <rect x="-2" y="-14" width="4" height="20" fill={P.cream} stroke={P.ink} strokeOpacity="0.6"/>
        <circle cx="0" cy="8" r="5" fill={P.wine}/>
        <rect x="-1" y="-10" width="2" height="16" fill={P.wine}/>
      </g>
    </>
  );
}

// 9. Solo acciaio — steel tank in cellar (no wood)
function SAcciaio({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={P.cream}/><stop offset="100%" stopColor="#e3d7be"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      {/* floor */}
      <rect x="0" y="108" width="200" height="22" fill={P.earthD} opacity="0.5"/>
      {/* two steel tanks */}
      {[[70],[140]].map(([cx],i)=>(
        <g key={i} transform={`translate(${cx} 30)`}>
          <ellipse cx="0" cy="0" rx="22" ry="6" fill={P.steel}/>
          <rect x="-22" y="0" width="44" height="72" fill={P.steel}/>
          <rect x="-22" y="0" width="6" height="72" fill={P.cream} opacity="0.35"/>
          <ellipse cx="0" cy="72" rx="22" ry="6" fill={P.steelD}/>
          {/* pipe/gauge */}
          <rect x="-6" y="30" width="12" height="8" fill={P.ink} opacity="0.55"/>
          <circle cx="0" cy="34" r="2" fill={P.gold}/>
          {/* legs */}
          <rect x="-18" y="76" width="3" height="8" fill={P.steelD}/>
          <rect x="15" y="76" width="3" height="8" fill={P.steelD}/>
        </g>
      ))}
    </>
  );
}

// 10. Vendemmia — hands with crate of grapes
function SVendemmia({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <path d="M0 70 Q80 60 200 68 L200 130 L0 130Z" fill={P.hillMid}/>
      <rect x="0" y="100" width="200" height="30" fill={P.earthD} opacity="0.35"/>
      {/* crate */}
      <g transform="translate(100 68)">
        <rect x="-32" y="10" width="64" height="26" fill={P.stone} stroke={P.stoneD} strokeWidth="1"/>
        {[-24,-8,8,24].map((x,i)=><line key={i} x1={x} y1="10" x2={x} y2="36" stroke={P.stoneD} strokeWidth="0.6"/>)}
        {/* grape bunches inside */}
        {[[-16,4],[0,-2],[16,4]].map(([cx,cy],i)=>(
          <g key={i} transform={`translate(${cx} ${cy})`}>
            {[[0,0],[3,0],[-3,0],[1.5,3],[-1.5,3],[0,6],[3,6],[-3,6]].map(([dx,dy],k)=>(
              <circle key={k} cx={dx} cy={dy+8} r="2" fill={P.wine} opacity="0.9"/>
            ))}
            <path d={`M0 6 Q3 4 6 6`} fill="none" stroke={P.leafD} strokeWidth="1"/>
            <ellipse cx="4" cy="4" rx="4" ry="2.5" fill={P.leaf}/>
          </g>
        ))}
      </g>
      {/* two hands holding the crate */}
      <g fill={P.earth}>
        <ellipse cx="60" cy="94" rx="10" ry="5"/>
        <rect x="55" y="92" width="10" height="12" rx="3"/>
        <ellipse cx="140" cy="94" rx="10" ry="5"/>
        <rect x="135" y="92" width="10" height="12" rx="3"/>
      </g>
    </>
  );
}

// 11. Mille bottiglie — row of numbered bottles
function SMille({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <rect x="0" y="108" width="200" height="22" fill={P.earthD} opacity="0.35"/>
      {[0,1,2,3,4].map(i=>(
        <g key={i} transform={`translate(${28+i*35} 30)`}>
          <path d="M-6 12 L-6 74 Q-6 80 -1 80 L1 80 Q6 80 6 74 L6 12 L4 12 L4 4 Q4 2 2 2 L-2 2 Q-4 2 -4 4 L-4 12Z" fill={P.rose}/>
          <rect x="-5" y="34" width="10" height="20" fill={P.cream} rx="0.5"/>
          <text x="0" y="46" fontSize="4.5" fontFamily="Montserrat, sans-serif" fill={P.wine} textAnchor="middle">{String(i*250+250).padStart(4,'0')}</text>
          <rect x="-3" y="-1" width="6" height="4" fill={P.ink}/>
        </g>
      ))}
    </>
  );
}

// 12. Cos'è slow wine — turtle carrying a wine glass (playful slow icon)
function SSlow({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <path d="M0 105 Q60 96 130 102 T200 100 L200 130 L0 130Z" fill={P.hillNear}/>
      {/* turtle */}
      <g transform="translate(100 88)">
        {/* legs */}
        <ellipse cx="-20" cy="8" rx="4" ry="3" fill={P.leafD}/>
        <ellipse cx="18" cy="8" rx="4" ry="3" fill={P.leafD}/>
        {/* body/shell */}
        <ellipse cx="0" cy="0" rx="24" ry="12" fill={P.leaf}/>
        <ellipse cx="0" cy="-2" rx="20" ry="9" fill={P.leafD}/>
        {[-10,0,10].map((x,i)=><circle key={i} cx={x} cy="-2" r="3.5" fill={P.leaf}/>)}
        {/* head */}
        <ellipse cx="-26" cy="0" rx="5" ry="4" fill={P.leafD}/>
        <circle cx="-28" cy="-1" r="0.9" fill={P.ink}/>
      </g>
      {/* wine glass on shell */}
      <g transform="translate(100 70)">
        <path d="M-6 0 Q-6 8 0 8 Q6 8 6 0 Z" fill={P.rose}/>
        <line x1="0" y1="8" x2="0" y2="14" stroke={P.ink} strokeWidth="1"/>
        <ellipse cx="0" cy="14" rx="4" ry="1" fill={P.ink}/>
      </g>
    </>
  );
}

// 13. Ritorno vigneron — figure walking uphill towards distant vineyard
function SRitorno({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#f6d5a8"/><stop offset="100%" stopColor="#eeb69b"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <circle cx="30" cy="42" r="10" fill={P.cream} opacity="0.9"/>
      <path d="M0 70 Q50 55 100 66 T200 60 L200 130 L0 130Z" fill={P.hillFar}/>
      <path d="M0 95 Q60 82 130 90 T200 88 L200 130 L0 130Z" fill={P.hillMid}/>
      <path d="M0 118 Q80 108 200 116 L200 130 L0 130Z" fill={P.hillNear}/>
      {/* winding path */}
      <path d="M60 130 Q80 116 100 106 T130 82" fill="none" stroke={P.stone} strokeWidth="3" strokeLinecap="round" opacity="0.9"/>
      {/* small figure walking up */}
      <g transform="translate(96 100)">
        <circle cx="0" cy="-6" r="3" fill={P.earth}/>
        <rect x="-2" y="-3" width="4" height="7" fill={P.wine}/>
        <line x1="-1" y1="4" x2="-3" y2="10" stroke={P.ink} strokeWidth="1.2"/>
        <line x1="1"  y1="4" x2="3" y2="10" stroke={P.ink} strokeWidth="1.2"/>
      </g>
      {/* distant vineyard rows */}
      {[[112,80],[122,74],[132,80],[142,76]].map(([x,y],i)=>(
        <rect key={i} x={x} y={y} width="6" height="1.5" fill={P.leafD}/>
      ))}
    </>
  );
}

// 14. Perché Lempole (nome) — an ampoule with a droplet
function SNome({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={P.sky}/><stop offset="100%" stopColor="#f0dcc4"/>
        </linearGradient>
        <linearGradient id={`rug-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={P.rose} stopOpacity="0.55"/>
          <stop offset="100%" stopColor={P.rose} stopOpacity="0.25"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>

      {/* soft nursery rug */}
      <ellipse cx="100" cy="120" rx="92" ry="12" fill={`url(#rug-${id})`}/>
      <ellipse cx="100" cy="121" rx="60" ry="4" fill={P.wine} opacity="0.08"/>

      {/* tiny floating hearts */}
      <path d="M46 30 q-3 -4 -6 -1 q-3 3 0 6 l6 6 l6 -6 q3 -3 0 -6 q-3 -3 -6 1Z" fill={P.wine} opacity="0.28"/>
      <path d="M156 40 q-2 -3 -4 -1 q-2 2 0 4 l4 4 l4 -4 q2 -2 0 -4 q-2 -2 -4 1Z" fill={P.wine} opacity="0.32"/>
      <path d="M172 22 q-1.5 -2 -3 -0.5 q-1.5 1.5 0 3 l3 3 l3 -3 q1.5 -1.5 0 -3 q-1.5 -1.5 -3 0.5Z" fill={P.wine} opacity="0.22"/>

      {/* Left girl (blonde) */}
      <g transform="translate(62 60)">
        {/* dress */}
        <path d="M-16 50 Q-18 22 0 22 Q18 22 16 50 Z" fill={P.rose}/>
        <path d="M-12 44 Q0 46 12 44" fill="none" stroke={P.cream} strokeWidth="0.8" opacity="0.7"/>
        {/* arm reaching toward centre plush */}
        <path d="M12 30 Q22 32 30 40" fill="none" stroke={P.cream} strokeWidth="5" strokeLinecap="round"/>
        {/* legs peeking */}
        <rect x="-8" y="50" width="5" height="6" fill={P.cream}/>
        <rect x="3" y="50" width="5" height="6" fill={P.cream}/>
        {/* head */}
        <circle cx="0" cy="10" r="11" fill={P.cream}/>
        {/* blonde hair */}
        <path d="M-11 8 Q-13 -6 0 -6 Q13 -6 11 8 Q9 3 4 2 Q0 1 -4 2 Q-9 3 -11 8Z" fill={P.gold}/>
        <path d="M-11 6 Q-14 20 -9 22" fill="none" stroke={P.gold} strokeWidth="3.2" strokeLinecap="round"/>
        <path d="M11 6 Q14 18 9 20" fill="none" stroke={P.gold} strokeWidth="3.2" strokeLinecap="round"/>
        {/* tiny bow */}
        <circle cx="-8" cy="2" r="1.8" fill={P.wine} opacity="0.7"/>
        {/* eyes */}
        <circle cx="-3.5" cy="10" r="1" fill={P.ink}/>
        <circle cx="3.5" cy="10" r="1" fill={P.ink}/>
        {/* cheeks */}
        <circle cx="-6.5" cy="13.5" r="1.6" fill={P.rose} opacity="0.7"/>
        <circle cx="6.5" cy="13.5" r="1.6" fill={P.rose} opacity="0.7"/>
        {/* smile */}
        <path d="M-2 15 Q0 17 2 15" fill="none" stroke={P.ink} strokeWidth="0.9" strokeLinecap="round"/>
      </g>

      {/* Right girl (brunette) */}
      <g transform="translate(138 60)">
        {/* dress */}
        <path d="M-16 50 Q-18 22 0 22 Q18 22 16 50 Z" fill={P.cyan}/>
        <path d="M-12 44 Q0 46 12 44" fill="none" stroke={P.cream} strokeWidth="0.8" opacity="0.7"/>
        {/* arm reaching toward centre plush */}
        <path d="M-12 30 Q-22 32 -30 40" fill="none" stroke={P.cream} strokeWidth="5" strokeLinecap="round"/>
        {/* legs */}
        <rect x="-8" y="50" width="5" height="6" fill={P.cream}/>
        <rect x="3" y="50" width="5" height="6" fill={P.cream}/>
        {/* head */}
        <circle cx="0" cy="10" r="11" fill={P.cream}/>
        {/* brunette hair with braid hint */}
        <path d="M-11 8 Q-13 -6 0 -6 Q13 -6 11 8 Q9 3 4 2 Q0 1 -4 2 Q-9 3 -11 8Z" fill={P.earthD}/>
        <path d="M-12 8 Q-15 22 -10 26" fill="none" stroke={P.earthD} strokeWidth="3.6" strokeLinecap="round"/>
        <path d="M12 8 Q15 22 10 26" fill="none" stroke={P.earthD} strokeWidth="3.6" strokeLinecap="round"/>
        {/* tiny bow */}
        <circle cx="8" cy="2" r="1.8" fill={P.wine} opacity="0.7"/>
        {/* eyes */}
        <circle cx="-3.5" cy="10" r="1" fill={P.ink}/>
        <circle cx="3.5" cy="10" r="1" fill={P.ink}/>
        <circle cx="-6.5" cy="13.5" r="1.6" fill={P.rose} opacity="0.7"/>
        <circle cx="6.5" cy="13.5" r="1.6" fill={P.rose} opacity="0.7"/>
        <path d="M-2 15 Q0 17 2 15" fill="none" stroke={P.ink} strokeWidth="0.9" strokeLinecap="round"/>
      </g>

      {/* the sloth plush cradled between them (actual Lempole sloth) */}
      <g>
        <ellipse cx="100" cy="112" rx="20" ry="3" fill={P.ink} opacity="0.12"/>
        <image href={SLOTH_URL} x="76" y="70" width="48" height="48" preserveAspectRatio="xMidYMid meet"/>
      </g>

      {/* handwritten "Lempole" nickname */}
      <text x="100" y="128" fontSize="8" fontFamily="Caveat, cursive" fill={P.wine} textAnchor="middle" fontStyle="italic">Lempole</text>
    </>
  );
}

// 15. Madonnino dei Campacci — votive shrine in field
function SMadonnino({ id }) {
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <path d="M0 82 Q60 72 130 78 T200 76 L200 130 L0 130Z" fill={P.hillFar}/>
      <path d="M0 102 Q60 92 130 98 T200 96 L200 130 L0 130Z" fill={P.hillMid}/>
      {/* small votive chapel centred */}
      <g transform="translate(100 60)">
        <rect x="-14" y="0" width="28" height="38" fill={P.cream}/>
        <polygon points="-16,0 16,0 0,-14" fill={P.wine}/>
        {/* arched niche */}
        <path d="M-8 12 L-8 32 L8 32 L8 12 Q0 6 -8 12Z" fill={P.hillNear}/>
        <circle cx="0" cy="18" r="3" fill={P.cream}/>
        {/* cross on top */}
        <rect x="-0.6" y="-24" width="1.2" height="10" fill={P.ink}/>
        <rect x="-3" y="-20" width="6" height="1.2" fill={P.ink}/>
      </g>
      {/* two cypresses flanking */}
      <ellipse cx="72" cy="94" rx="3.5" ry="12" fill={P.leafD}/>
      <ellipse cx="128" cy="94" rx="3.5" ry="12" fill={P.leafD}/>
      {/* small path leading to shrine */}
      <path d="M100 118 Q100 108 100 100" fill="none" stroke={P.stone} strokeWidth="3" strokeLinecap="round" opacity="0.8"/>
    </>
  );
}

// 16. Calice giusto — a wine glass with pale rosé
function SCalice({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={P.cream}/><stop offset="100%" stopColor="#f0dfca"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <ellipse cx="100" cy="118" rx="60" ry="5" fill={P.earthD} opacity="0.35"/>
      <g transform="translate(100 30)">
        {/* bowl */}
        <path d="M-24 0 Q-24 32 0 48 Q24 32 24 0Z" fill="none" stroke={P.ink} strokeWidth="1.4"/>
        {/* wine inside */}
        <path d="M-20 8 Q-20 28 0 44 Q20 28 20 8Z" fill={P.rose} opacity="0.85"/>
        {/* highlight */}
        <ellipse cx="-9" cy="18" rx="3" ry="8" fill={P.cream} opacity="0.7"/>
        {/* stem */}
        <line x1="0" y1="48" x2="0" y2="72" stroke={P.ink} strokeWidth="1.4"/>
        {/* foot */}
        <ellipse cx="0" cy="72" rx="14" ry="2" fill={P.ink}/>
      </g>
    </>
  );
}

// 17. Abbinamenti — table setting: glass, plate, fork
function SAbb({ id }) {
  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={P.cream}/><stop offset="100%" stopColor="#efe3cf"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      {/* table linen */}
      <rect x="0" y="86" width="200" height="44" fill={P.rose} opacity="0.35"/>
      {/* plate */}
      <ellipse cx="100" cy="98" rx="42" ry="14" fill={P.cream} stroke={P.stoneD} strokeWidth="0.6"/>
      <ellipse cx="100" cy="98" rx="32" ry="9" fill="none" stroke={P.stoneD} strokeWidth="0.5" opacity="0.6"/>
      {/* food element (bruschetta) */}
      <ellipse cx="100" cy="94" rx="16" ry="4" fill={P.field}/>
      <circle cx="94" cy="93" r="1.5" fill={P.wine}/>
      <circle cx="102" cy="93" r="1.5" fill={P.wine}/>
      <circle cx="108" cy="94" r="1.5" fill={P.leaf}/>
      {/* wine glass right */}
      <g transform="translate(160 60)">
        <path d="M-8 0 Q-8 14 0 20 Q8 14 8 0Z" fill={P.rose}/>
        <line x1="0" y1="20" x2="0" y2="34" stroke={P.ink} strokeWidth="1"/>
        <ellipse cx="0" cy="34" rx="6" ry="1" fill={P.ink}/>
      </g>
      {/* fork left */}
      <g transform="translate(40 62) rotate(-4)" stroke={P.ink} strokeWidth="1" strokeLinecap="round" fill="none">
        <line x1="0" y1="0" x2="0" y2="34"/>
        <line x1="-3" y1="0" x2="-3" y2="7"/>
        <line x1="3" y1="0" x2="3" y2="7"/>
      </g>
    </>
  );
}

// 18. Un anno in vigna — circular seasonal cycle
function SAnno({ id }) {
  const cx = 100, cy = 66, r = 44;
  const seasons = [
    { a: -90, col: P.cyan,     ic: 'winter' },
    { a:   0, col: P.leaf,     ic: 'spring' },
    { a:  90, col: P.field,    ic: 'summer' },
    { a: 180, col: P.wine,     ic: 'autumn' },
  ];
  return (
    <>
      <Sky id={id}/>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      <circle cx={cx} cy={cy} r={r+8} fill={P.cream} opacity="0.85"/>
      {seasons.map((s,i)=>{
        const rad = (s.a * Math.PI) / 180;
        const x = cx + Math.cos(rad) * r;
        const y = cy + Math.sin(rad) * r;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="10" fill={s.col} opacity="0.9"/>
            {s.ic === 'winter' && <circle cx={x} cy={y} r="4" fill={P.cream}/>}
            {s.ic === 'spring' && <ellipse cx={x} cy={y} rx="5" ry="2.5" fill={P.leafD}/>}
            {s.ic === 'summer' && <circle cx={x} cy={y} r="5" fill={P.gold}/>}
            {s.ic === 'autumn' && <path d={`M${x-3} ${y+2} Q${x} ${y-5} ${x+3} ${y+2}Z`} fill={P.field}/>}
          </g>
        );
      })}
      {/* central vine plant */}
      <g transform={`translate(${cx} ${cy})`}>
        <rect x="-1" y="-8" width="2" height="16" fill={P.earthD}/>
        <circle cx="-6" cy="-6" r="4" fill={P.leaf}/>
        <circle cx="6" cy="-4" r="4" fill={P.leafD}/>
        <circle cx="0" cy="-12" r="3.5" fill={P.leaf}/>
      </g>
      {/* connecting circle */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={P.ink} strokeWidth="0.5" strokeDasharray="2 3" opacity="0.35"/>
    </>
  );
}

/* --------------------------- MAPPING --------------------------------- */

// 19. Note sull'Annata 2025 — "la prima vendemmia" — emotional Tuscan sunset scene
function SAnnata25({ id }) {
  return (
    <>
      <defs>
        {/* Pre-dawn sky: cold indigo at top fading into warm rose at horizon,
            no text or labels — just the vineyard waking up. */}
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#3a3f5f"/>
          <stop offset="45%" stopColor="#a35e6a"/>
          <stop offset="80%" stopColor="#f2b98a"/>
          <stop offset="100%" stopColor="#f6d7a4"/>
        </linearGradient>
        <radialGradient id={`sun-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff4d8" stopOpacity="0.95"/>
          <stop offset="60%" stopColor="#f6c778" stopOpacity="0.5"/>
          <stop offset="100%" stopColor="#f6c778" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id={`mist-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#fbf6ea" stopOpacity="0"/>
          <stop offset="60%" stopColor="#fbf6ea" stopOpacity="0.55"/>
          <stop offset="100%" stopColor="#fbf6ea" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <rect width="200" height="130" fill={`url(#sky-${id})`}/>

      {/* Rising sun — low on the horizon, warm halo bleeding into the mist. */}
      <circle cx="150" cy="82" r="48" fill={`url(#sun-${id})`}/>
      <circle cx="150" cy="82" r="8" fill="#fff2cf" opacity="0.95"/>

      {/* Distant ridges — layered silhouettes from cool to warm. */}
      <path d="M0 82 Q45 66 90 76 T200 72 L200 130 L0 130 Z" fill="#5a5e70" opacity="0.6"/>
      <path d="M0 90 Q50 76 100 84 T200 82 L200 130 L0 130 Z" fill="#6d5f6a" opacity="0.75"/>
      <path d="M0 96 Q40 84 90 92 T200 90 L200 130 L0 130 Z" fill="#7a6558" opacity="0.85"/>

      {/* Cypress on the far ridge. */}
      <path d="M158 78 Q160 66 162 78 L162 88 L158 88 Z" fill="#3f4a3a" opacity="0.8"/>
      <path d="M42 86 Q43.5 76 45 86 L45 94 L42 94 Z" fill="#3f4a3a" opacity="0.75"/>

      {/* Low mist floating over the middle rows. */}
      <rect x="0" y="86" width="200" height="14" fill={`url(#mist-${id})`}/>

      {/* Foreground vineyard rows in perspective — clean, no numbers, no labels. */}
      <g stroke="#4a3a30" strokeWidth="0.7" strokeLinecap="round" opacity="0.85">
        <line x1="4"   y1="118" x2="60"  y2="100"/>
        <line x1="28"  y1="122" x2="82"  y2="102"/>
        <line x1="58"  y1="126" x2="108" y2="104"/>
        <line x1="92"  y1="128" x2="138" y2="106"/>
        <line x1="130" y1="129" x2="172" y2="108"/>
        <line x1="168" y1="129" x2="198" y2="110"/>
      </g>
      {/* Little vine stakes catching the first light. */}
      <g fill="#2b2018">
        {[10,22,34,46,58].map((x,i)=><circle key={'a'+i} cx={x} cy={118 - i*0.8} r="0.9"/>)}
        {[34,50,66,82,98].map((x,i)=><circle key={'b'+i} cx={x} cy={122 - i*0.8} r="0.9"/>)}
        {[70,88,106,124,142].map((x,i)=><circle key={'c'+i} cx={x} cy={126 - i*0.8} r="0.9"/>)}
      </g>

      {/* A single vine silhouette near the camera to anchor the scene. */}
      <g transform="translate(24 108)" opacity="0.9">
        <path d="M0 0 L0 -14" stroke="#2b2018" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M0 -14 Q-6 -18 -8 -22 M0 -14 Q6 -18 9 -22" stroke="#2b2018" strokeWidth="0.9" fill="none" strokeLinecap="round"/>
        <circle cx="-6" cy="-19" r="1.4" fill="#2b2018"/>
        <circle cx="7"  cy="-20" r="1.4" fill="#2b2018"/>
      </g>
    </>
  );
}

// Sparkling wine bottle — stylised silhouette with bubbles rising inside the
// body. Warm cellar backdrop, no writing, in the same flat-illustration
// language of the other diary scenes.
function SPupitre({ id }) {
  // Rising bubbles inside the bottle body — sizes and vertical positions
  // chosen by hand to feel effervescent rather than uniform.
  const bubbles = [
    { x: 92,  y: 108, r: 1.4 },
    { x: 106, y: 104, r: 1.0 },
    { x: 98,  y: 100, r: 1.8 },
    { x: 110, y: 96,  r: 1.2 },
    { x: 90,  y: 92,  r: 1.5 },
    { x: 102, y: 88,  r: 1.0 },
    { x: 112, y: 84,  r: 1.6 },
    { x: 96,  y: 80,  r: 1.1 },
    { x: 104, y: 74,  r: 1.4 },
    { x: 100, y: 68,  r: 1.0 },
    { x: 108, y: 62,  r: 1.2 },
    { x: 98,  y: 56,  r: 0.9 },
    { x: 103, y: 48,  r: 0.8 },
  ];

  return (
    <>
      <defs>
        <linearGradient id={`sky-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#f6e2c0"/>
          <stop offset="100%" stopColor="#e6c898"/>
        </linearGradient>
        {/* Glass gradient — dark bottle-green with a slightly lighter belly so
            the silhouette reads volumetric even though the drawing is flat. */}
        <linearGradient id={`glass-${id}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%"  stopColor="#1f3020"/>
          <stop offset="45%" stopColor="#3a5236"/>
          <stop offset="100%" stopColor="#1f3020"/>
        </linearGradient>
        {/* Pale liquid inside — just visible enough to feel like a rosé
            sparkling, without dominating the composition. */}
        <linearGradient id={`liq-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#f3c9ba" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#e6a58f" stopOpacity="0.35"/>
        </linearGradient>
        <radialGradient id={`glow-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#fff4d8" stopOpacity="0.9"/>
          <stop offset="70%" stopColor="#f2b98a" stopOpacity="0.25"/>
          <stop offset="100%" stopColor="#f2b98a" stopOpacity="0"/>
        </radialGradient>
      </defs>

      <rect width="200" height="130" fill={`url(#sky-${id})`}/>
      {/* Warm halo behind the bottle so the eye lands centre. */}
      <ellipse cx="100" cy="70" rx="72" ry="52" fill={`url(#glow-${id})`}/>

      {/* ----- Bottle silhouette ------------------------------------------- */}
      {/* Foil capsule (muselet cap). */}
      <path d="M96 14 L104 14 L105 26 L95 26 Z" fill="#7a3520"/>
      <rect x="95" y="24" width="10" height="2" fill="#5a1e14"/>

      {/* Wire cage hint — two tiny horizontal marks below the cap. */}
      <line x1="94" y1="30" x2="106" y2="30" stroke="#c9a463" strokeWidth="0.5" opacity="0.85"/>
      <line x1="94" y1="32" x2="106" y2="32" stroke="#c9a463" strokeWidth="0.4" opacity="0.7"/>

      {/* Bottle glass — neck, sloping shoulder, body, base — all one shape. */}
      <path
        d="M96 26
           L104 26
           L104 52
           C 118 60, 122 68, 122 78
           L 122 116
           C 122 118, 120 119, 118 119
           L 82 119
           C 80 119, 78 118, 78 116
           L 78 78
           C 78 68, 82 60, 96 52
           Z"
        fill={`url(#glass-${id})`}
      />

      {/* Liquid tint inside body only (visible via a lighter panel that
          hints at content). Clipped roughly to the belly outline. */}
      <path
        d="M84 74
           C 84 68, 88 62, 96 58
           L104 58
           C 112 62, 116 68, 116 74
           L 116 113
           C 116 115, 115 116, 113 116
           L 87 116
           C 85 116, 84 115, 84 113
           Z"
        fill={`url(#liq-${id})`}
      />

      {/* Rising bubbles inside the bottle. */}
      {bubbles.map((b, k) => (
        <circle
          key={k}
          cx={b.x}
          cy={b.y}
          r={b.r}
          fill="#fbf6ea"
          opacity="0.85"
        />
      ))}
      {bubbles.map((b, k) => (
        <circle
          key={`h${k}`}
          cx={b.x - b.r * 0.35}
          cy={b.y - b.r * 0.35}
          r={b.r * 0.35}
          fill="#ffffff"
          opacity="0.9"
        />
      ))}

      {/* Glass highlight — soft vertical sliver on the left side. */}
      <path
        d="M84 76 C 82 92, 82 106, 84 114"
        stroke="#eae0c9"
        strokeWidth="0.9"
        strokeLinecap="round"
        fill="none"
        opacity="0.35"
      />

      {/* Minimal label band — cream, no writing, just a hint of paper. */}
      <rect x="82" y="86" width="36" height="18" fill="#f6e6cd" opacity="0.92"/>
      <line x1="82" y1="86" x2="118" y2="86" stroke="#c9a463" strokeWidth="0.4" opacity="0.6"/>
      <line x1="82" y1="104" x2="118" y2="104" stroke="#c9a463" strokeWidth="0.4" opacity="0.6"/>
      {/* Small decorative dot centred on the label — a discreet mark, not a logo. */}
      <circle cx="100" cy="95" r="1.4" fill="#7a3520" opacity="0.8"/>

      {/* Ground shadow */}
      <ellipse cx="100" cy="122" rx="34" ry="2.4" fill="#3a2418" opacity="0.22"/>
    </>
  );
}

const SCENES = {
  'perche-un-bradipo':                SBradipo,
  'lamole-cru-silenzioso-chianti-classico': SLamole,
  'viticoltura-eroica-terrazzamenti': STerraceEroica,
  'muretti-a-secco-paesaggio':        SMuretto,
  'sangiovese-rosato-contro-corrente':SRosato,
  'bio-alta-quota-600-metri':         SBio,
  'biodiversita-in-vigna':            SBiodiv,
  'escursione-termica-lamole':        SEscur,
  'solo-acciaio-perche-no-legno':     SAcciaio,
  'vendemmia-manuale-40-quintali':    SVendemmia,
  'mille-bottiglie-numerate':         SMille,
  'cos-e-slow-wine':                  SSlow,
  'ritorno-vigneron-lamole':          SRitorno,
  'perche-lempole-nome':              SNome,
  'madonnino-dei-campacci':           SMadonnino,
  'calice-giusto-arte-incontro':      SCalice,
  'abbinamenti-lempole-tavola':       SAbb,
  'un-anno-in-vigna-mese-per-mese':   SAnno,
  'note-annata-2025':                 SAnnata25,
  'primo-passo-bollicina':            SPupitre,
};

export default function BlogSketch({ slug, className = '', ariaLabel }) {
  const Scene = SCENES[slug] || SLamole;
  const id = 's-' + (slug || 'x');
  return (
    <svg
      viewBox={VB}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={ariaLabel || slug || 'diary illustration'}
      preserveAspectRatio="xMidYMid slice"
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      <Scene id={id} />
    </svg>
  );
}
